/**
 * The one place the app talks HTTP. Same-origin JSON, the session rides in the
 * first-party `ch_session` cookie, and every failure becomes an ApiError with
 * a message that is safe to show.
 */

export type ErrorBody = { error?: string; details?: string[] };

export class ApiError extends Error {
  readonly status: number;
  readonly details: string[];
  readonly path: string;

  constructor(status: number, message: string, path: string, details: string[] = []) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
    this.path = path;
  }

  /** The request never got an HTTP answer (offline, server down, CORS). */
  get isNetwork(): boolean {
    return this.status === 0;
  }
  get isUnauthorized(): boolean {
    return this.status === 401;
  }
  get isForbidden(): boolean {
    return this.status === 403;
  }
  get isNotFound(): boolean {
    return this.status === 404;
  }
  get isConflict(): boolean {
    return this.status === 409;
  }
  get isRateLimited(): boolean {
    return this.status === 429;
  }
  /**
   * The server has no such route (yet). Its catch-all answers a bare
   * "not found", while a missing record carries a specific message such as
   * "event not found". The UI uses this to show "coming soon" for endpoints
   * that are specified but not deployed.
   */
  get isUnavailable(): boolean {
    return this.status === 404 && this.message === 'not found';
  }
}

/** Human copy for an error, for toasts and inline error states. */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.isNetwork) return "Can't reach CertiHash. Check your connection and try again.";
    if (err.isRateLimited) return 'Too many requests. Wait a moment and try again.';
    if (err.status >= 500) return 'Something went wrong on our side. Try again in a moment.';
    if (err.isUnauthorized && err.message === 'sign in required') return 'Your session has ended. Sign in again.';
    if (err.details.length > 0) return `${capitalize(err.message)}: ${err.details.join('; ')}`;
    return capitalize(err.message);
  }
  if (err instanceof Error) return err.message;
  return 'Something went wrong.';
}

function capitalize(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

type Query = Record<string, string | number | boolean | null | undefined>;

export type RequestOptions = {
  body?: unknown;
  query?: Query;
  /** Non-2xx statuses whose JSON body is a valid answer (e.g. 409 for a failed proof). */
  acceptStatuses?: number[];
  signal?: AbortSignal;
};

export function buildPath(path: string, query?: Query): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

export async function request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
  const url = `/v1${buildPath(path, opts.query)}`;
  const headers: Record<string, string> = { accept: 'application/json' };
  if (opts.body !== undefined) headers['content-type'] = 'application/json';

  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers,
      credentials: 'same-origin',
      ...(opts.body !== undefined && { body: JSON.stringify(opts.body) }),
      ...(opts.signal && { signal: opts.signal }),
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiError(0, 'network error', path);
  }

  const text = await res.text();
  let body: unknown = undefined;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = undefined;
    }
  }

  if (res.ok || opts.acceptStatuses?.includes(res.status)) {
    return body as T;
  }

  const eb = (body ?? {}) as ErrorBody;
  const message = typeof eb.error === 'string' && eb.error ? eb.error : res.statusText || `HTTP ${res.status}`;
  throw new ApiError(res.status, message, path, Array.isArray(eb.details) ? eb.details : []);
}

export const http = {
  get: <T>(path: string, opts?: Omit<RequestOptions, 'body'>) => request<T>('GET', path, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>('POST', path, { ...opts, body: body ?? {} }),
  patch: <T>(path: string, body: unknown, opts?: RequestOptions) => request<T>('PATCH', path, { ...opts, body }),
  del: <T>(path: string, opts?: RequestOptions) => request<T>('DELETE', path, opts),
};
